import React, { useState } from "react";
import { Eye, Link as LinkIcon, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Document Standard (GDrive-Only) — every Drive-linked cell across the
 * Revenue/Expenses/Petty Cash ledgers used to show the full raw URL as an
 * editable text input, which made those columns unreadably wide.
 *
 * Feb 2026 UI Refinement: replaced with a single compact icon button —
 * orange/filled when a link is on file, muted/outline when empty. Clicking
 * it opens a small popover with the actual paste-a-link input; a separate
 * Eye icon (only shown once a link exists) opens the shared in-app preview
 * (DriveLinkPreviewModal). No file ever touches our own object storage —
 * Drive is still the single source of truth, just no longer shown inline.
 */
export default function DriveLinkCell({ value, onChange, onPreview, placeholder = "Paste Drive link", testId, readOnly = false }) {
  const [draft, setDraft] = useState(value || "");
  const [open, setOpen] = useState(false);
  const hasValue = !!value;

  if (readOnly) {
    return hasValue ? (
      <button type="button" onClick={() => onPreview(value)} className="text-[#F54800] hover:text-[#D63E00]" title="Preview in Drive" data-testid={testId}>
        <FileText size={15} />
      </button>
    ) : <span className="text-xs text-slate-300">—</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(value || ""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`p-1.5 rounded-sm border transition-colors ${hasValue ? "border-orange-200 bg-orange-50 text-[#F54800] hover:bg-orange-100" : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"}`}
            title={hasValue ? "Edit Drive link" : "Add Drive link"}
            data-testid={testId}
          >
            <LinkIcon size={13} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2 rounded-sm" align="start">
          <Input
            autoFocus
            className="rounded-sm text-xs"
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (draft !== (value || "")) onChange(draft); }}
            onKeyDown={(e) => { if (e.key === "Enter") { onChange(draft); setOpen(false); } }}
            data-testid={testId ? `${testId}-input` : undefined}
          />
        </PopoverContent>
      </Popover>
      {hasValue && (
        <button type="button" onClick={() => onPreview(value)} className="shrink-0 text-slate-400 hover:text-[#F54800]" title="Preview in Drive" data-testid={testId ? `${testId}-preview` : undefined}>
          <Eye size={13} />
        </button>
      )}
    </div>
  );
}

